import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  FormControlLabel,
  Switch,
  Typography,
  IconButton,
  Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useDraggable } from "../../hooks/useDraggable";
import {
  createTransaction,
  NewTransactionData,
  Category,
  getCategoriesByType,
  getUserTransaction,
  UserTransaction,
} from "../../services/api";
import { useSnackbar } from "notistack";
import CurrencyInput from "react-currency-input-field";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Dayjs } from "dayjs";

const monthOptions = Array.from({ length: 12 }, (_, i) => ({
  value: i + 1,
  label: new Date(0, i).toLocaleString("pt-BR", { month: "long" }),
}));

const generateYearOptions = () => {
  const years = [];
  for (let year = 2024; year <= 2030; year++) {
    years.push(year);
  }
  return years;
};
const yearOptions = generateYearOptions();

interface TransactionModalProps {
  open: boolean;
  onClose: () => void;
  type: "Entrada" | "Saída";
  onSaveSuccess: (newTransaction: any) => void;
}

export const TransactionModal = ({
  open,
  onClose,
  type,
  onSaveSuccess,
}: TransactionModalProps) => {
  const [name, setName] = useState("");
  const [value, setValue] = useState<number | undefined>();
  const [tccId, setTccId] = useState("");
  const [tscRecurrence, setTscRecurrence] = useState(false);
  const [tscCheck, setTscCheck] = useState(false);
  const [tscRecurrenceDate, setTscRecurrenceDate] = useState<Dayjs | null>(
    null
  );
  const [tscMonth, setTscMonth] = useState<number>(new Date().getMonth() + 1);
  const [tscYear, setTscYear] = useState<number>(new Date().getFullYear());
  const [usrIdTransaction, setUsrIdTransaction] = useState("");

  const [categories, setCategories] = useState<Category[]>([]);
  const [userTransactions, setUserTransactions] = useState<UserTransaction[]>(
    []
  );
  const [isCategoriesLoading, setIsCategoriesLoading] = useState(false);
  const [isUserTransactionLoading, setIsUserTransactionLoading] =
    useState(false);

  const [isLoading, setIsLoading] = useState(false);

  // 1. Adicionamos um estado para a 'key' do input, para podermos resetá-lo
  const [inputKey, setInputKey] = useState(`input-${Date.now()}`);

  const { enqueueSnackbar } = useSnackbar();
  const { elementRef, style, resetPosition } = useDraggable('#draggable-dialog-title');

  useEffect(() => {
    // Só busca se o modal estiver aberto
    if (open) {
      const fetchCategories = async () => {
        setIsCategoriesLoading(true);
        try {
          const tctId = type === "Entrada" ? 1 : 2;
          const fetchedCategories = await getCategoriesByType(tctId);
          setCategories(fetchedCategories);
        } catch (error) {
          enqueueSnackbar("Erro ao carregar categorias.", { variant: "error" });
        } finally {
          setIsCategoriesLoading(false);
        }
      };

      const fetchUserTransaction = async () => {
        setIsUserTransactionLoading(true);
        try {
          const fetchedUserTransactions = await getUserTransaction();
          setUserTransactions(fetchedUserTransactions);
        } catch (error) {
          enqueueSnackbar("Erro ao carregar Usuários.", { variant: "error" });
        } finally {
          setIsUserTransactionLoading(false);
        }
      };
      fetchCategories();
      fetchUserTransaction();
    }
  }, [open, type, enqueueSnackbar]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsLoading(true);
    if (!value || value <= 0 || !tccId) {
      enqueueSnackbar("Por favor, preencha todos os campos.", {
        variant: "warning",
      });
      setIsLoading(false);
      return;
    }

    if (tscRecurrenceDate && !tscRecurrenceDate) {
      enqueueSnackbar(
        "A data da ocorrência é obrigatória para transações recorrentes.",
        { variant: "warning" }
      );
      setIsLoading(false);
      return;
    }

    const transactionData: NewTransactionData = {
      tscName: name,
      tscValue: value,
      tctId: type === "Entrada" ? 1 : 2,
      tccId: parseInt(tccId, 10),
      tscRecurrence: tscRecurrence ? 1 : 0,
      tscCheck: tscCheck ? 1 : 0,
      tscRecurrenceDate: tscRecurrenceDate
        ? tscRecurrenceDate?.format("YYYY-MM-DD")
        : null,
      usrId: 1,
      tscMonth: tscMonth,
      tscYear: tscYear,
      usrIdTransaction: parseInt(usrIdTransaction, 10),
    };
    try {
      const novaTransacaoCriada = await createTransaction(transactionData);
      enqueueSnackbar("Transação salva com sucesso!", { variant: "success" });
      onSaveSuccess(novaTransacaoCriada);
      handleClose();
    } catch (err: any) {
      enqueueSnackbar(err.error || "Falha ao salvar. Tente novamente.", {
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setName("");
    setValue(undefined);
    setTccId("");
    setTscRecurrence(false);
    setTscCheck(false);
    setTscRecurrenceDate(null);
    setTscMonth(new Date().getMonth() + 1);
    setTscYear(new Date().getFullYear());
    setUsrIdTransaction("");
    setInputKey(`input-${Date.now()}`);
    resetPosition();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        ref: elementRef,
        sx: {
          borderRadius: 2,
          minHeight: "400px",
          ...style,
        },
      }}
    >
      <DialogTitle
        id="draggable-dialog-title"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "#26372D",
          color: "#fff",
          padding: "16px 24px",
          cursor: "move",
        }}
      >
        Adicionar Nova {type}
        <IconButton
          onClick={handleClose}
          sx={{ color: "#fff", padding: 0 }}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ padding: "24px" }}>
          <TextField
            autoFocus
            margin="dense"
            label="Descrição da Transação"
            type="text"
            fullWidth
            variant="outlined"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />

          <FormControl fullWidth margin="dense" required>
            <InputLabel id="category-select-label">Categoria</InputLabel>
            <Select
              labelId="category-select-label"
              value={tccId}
              label="Categoria"
              onChange={(e: SelectChangeEvent) => setTccId(e.target.value)}
              disabled={isCategoriesLoading}
            >
              {isCategoriesLoading ? (
                <MenuItem disabled>Carregando...</MenuItem>
              ) : (
                categories.map((category) => (
                  <MenuItem key={category.tccId} value={category.tccId}>
                    {category.tccName}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          {type === "Saída" && (
            <FormControlLabel
              control={
                <Switch
                  checked={tscCheck}
                  onChange={(e) => setTscCheck(e.target.checked)}
                  name="check"
                  color="primary"
                />
              }
              label="Pagamento Efetuado?"
              style={{ marginTop: "10px", display: "block" }}
            />
          )}

          <FormControlLabel
            control={
              <Switch
                checked={tscRecurrence}
                onChange={(e) => setTscRecurrence(e.target.checked)}
                name="recorrencia"
                color="primary"
              />
            }
            label="É uma transação recorrente?"
            style={{ marginTop: "10px", display: "block" }}
          />

          {tscRecurrence && (
            <DatePicker
              label="Fim da Recorrência"
              value={tscRecurrenceDate}
              onChange={(newValue) => setTscRecurrenceDate(newValue)}
              sx={{ width: "100%", marginTop: "10px", marginBottom: "10px" }}
            />
          )}

          <div style={{ display: "flex", gap: "10px" }}>
            <FormControl fullWidth margin="dense" required>
              <InputLabel id="month-select-label">Mês</InputLabel>
              <Select
                labelId="month-select-label"
                value={tscMonth}
                label="Mês"
                onChange={(e: SelectChangeEvent<number>) =>
                  setTscMonth(e.target.value as number)
                }
              >
                {monthOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label.charAt(0).toUpperCase() +
                      option.label.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth margin="dense" required>
              <InputLabel id="year-select-label">Ano</InputLabel>
              <Select
                labelId="year-select-label"
                value={tscYear}
                label="Ano"
                onChange={(e: SelectChangeEvent<number>) =>
                  setTscYear(e.target.value as number)
                }
              >
                {yearOptions.map((y) => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          <FormControl
            fullWidth
            margin="dense"
            sx={{ marginBottom: "10px" }}
            required
          >
            <InputLabel id="user-transaction-select-label">
              Usuário da Transação
            </InputLabel>
            <Select
              labelId="user-transaction-select-label"
              value={usrIdTransaction}
              label="Usuário da Transação"
              onChange={(e: SelectChangeEvent) =>
                setUsrIdTransaction(e.target.value)
              }
              disabled={isUserTransactionLoading}
            >
              {isUserTransactionLoading ? (
                <MenuItem disabled>Carregando...</MenuItem>
              ) : (
                userTransactions.map((user) => (
                  <MenuItem key={user.usrId} value={user.usrId}>
                    {user.usrName}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          <CurrencyInput
            key={inputKey}
            id="valor-transacao"
            name="valor"
            placeholder="R$ 0,00"
            defaultValue={0}
            onValueChange={(value, name, values) =>
              setValue(values?.float ?? undefined)
            }
            intlConfig={{ locale: "pt-BR", currency: "BRL" }}
            customInput={TextField}
            required
          />
        </DialogContent>
        <DialogActions
          sx={{
            padding: "16px 24px",
            backgroundColor: "#f8f9fa",
            justifyContent: "flex-end",
            gap: 1,
          }}
        >
          <Button
            onClick={handleClose}
            variant="outlined"
            disabled={isLoading}
            sx={{
              color: "#26372D",
              borderColor: "#26372D",
              "&:hover": {
                borderColor: "#26372D",
                backgroundColor: "rgba(38, 55, 45, 0.04)",
              },
            }}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
            sx={{
              backgroundColor: "#26372D",
              "&:hover": {
                backgroundColor: "#1e2a22",
              },
              "&:disabled": {
                backgroundColor: "#ccc",
              },
            }}
          >
            {isLoading ? <CircularProgress size={24} /> : "Salvar"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
